import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";

import { useQuery } from "@tanstack/react-query";
import {
  CheckIcon,
  CircleAlertIcon,
  CircleQuestionMarkIcon,
  EyeIcon,
  FileTextIcon,
  SheetIcon,
} from "lucide-react";
import { useSessionStore } from "stores/session";

import { formatDateAt } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { List, ListItem } from "simplydo/react/ui/list";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import { IdeaBusinessProfileChip } from "components/lib/Chips";
import CircularProgress from "components/lib/CircularProgress";
import IdeaPdfExport from "components/lib/IdeaPdfExport";
import IdeaDisplayFields from "components/lib/Ideas/IdeaDisplayFields";
import { HandledIdeaPreview } from "components/lib/Ideas/IdeaPreview";
import { useTrayStatus } from "components/lib/Notifications";
import ProductTour from "components/lib/ProductTour";
import { FakeLink } from "components/lib/UI";

import type { ChallengeDetail, IdeaTemplateAssessmentRound } from "../../types";

export const AssessmentClosureInfo = ({
  ideaTemplate,
  assessmentRound,
}: {
  ideaTemplate?: OpenAPI.Schemas["IdeaTemplate"];
  assessmentRound?: IdeaTemplateAssessmentRound;
}) => {
  const { t } = useTranslation();

  if (!ideaTemplate || !assessmentRound) return null;
  const assessmentDeadline = assessmentRound.assessmentDeadline || false;
  const assessmentClosed = assessmentRound.assessmentClosed || false;

  if (assessmentClosed) {
    return (
      <Message color="orange" size="small" style={{ marginBottom: 25 }}>
        <p>
          <b>Assessment closed:</b> Assessment is now closed for this{" "}
          {t("generic.challenge")}. You can continue to view your assigned ideas
          and previous assessments. If you think this is in error, please
          contact an administrator.
        </p>
      </Message>
    );
  }
  if (assessmentDeadline) {
    return (
      <Message
        color="blue"
        size="small"
        style={{ marginBottom: 25 }}
        className="assessment-tour-step-6"
      >
        <MessageHeader>Assessment deadline</MessageHeader>
        <MessageContent>
          Assessment for this {t("generic.challenge")} has a deadline on{" "}
          <b>{formatDateAt(assessmentDeadline)}</b>. You cannot edit or submit
          assessments after this date.
        </MessageContent>
      </Message>
    );
  }
  return null;
};

/* Note that myAssignedAssessments is a list of ideas, each with an assessment object, which might contain an assessments array. Each entry in the assessments array is an assessment made by an individual assessor, which might contain a (deeper) assessment object, which references individual idea sections.
 * Example myAssignedAssessments: [
 * {
 *    name: 'Idea name',
 *    _id: 'IdeaID',
 *    assessments: [ // array of assessor's assessments
 *      {
 *        score: 75, // total calculated score across this assessor based on total available
 *        ownerUser: userObject,
 *        assessment: {
 *          'section1ID': {
 *            score: 15, // assessor's score for this section,
 *            notes: 'Notes for this section'
 *          }, ...
 *        },
 *      }, ...
 *    ]
 *  }, ...
 * ]
 *
 * Note that whilst other assessor's assessments may be present in the DB and their names included in the array, if scoped to a single assessor then the total calculated score will only take into account the scoped assessment.
 */

type YourAssessmentRoundProps = {
  challenge: ChallengeDetail;
  assessmentRound: IdeaTemplateAssessmentRound;
};

const YourAssessmentRound = ({
  challenge,
  assessmentRound,
}: YourAssessmentRoundProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useCurrentUser();

  const urlOrganisation = useSessionStore((state) => state.urlOrganisation);
  const demoingTour = useSessionStore((state) => state.demoingTour);

  const [searchParams] = useSearchParams();
  const [previewId, setPreviewId] = useState<string | null>(
    searchParams.get("idea"),
  );
  const [tourCheckLoaded, setTourCheckLoaded] = useState(false);

  const [_, setTrayStatus] = useTrayStatus();
  const [assessmentSubmissionModalOpen, setAssessmentSubmissionModalOpen] =
    useState(false);
  const { setAssessmentRound } = useAssessmentEditor(null);
  const { ownerIdeaTemplate } = challenge || {};

  // If we are about to run the assessment tour, we do not display the idea preview
  const getTour = useCallback(() => {
    const organisationId = user?.ownerOrganisation?._id ?? urlOrganisation?._id;
    if (organisationId && !theme.sizes.isMobile) {
      api.organisations.getProductTour(
        organisationId,
        "assessment",
        ({ success, userHasCompletedTour }) => {
          if (
            success &&
            (!userHasCompletedTour || demoingTour === "assessment")
          ) {
            navigate({ search: "" }, { replace: true });
            setPreviewId("");
          }
          setTourCheckLoaded(true);
        },
        () => setTourCheckLoaded(true),
      );
    } else {
      setTourCheckLoaded(true);
    }
  }, [user, demoingTour, urlOrganisation, navigate, theme.sizes.isMobile]);

  useEffect(() => {
    getTour();
  }, [getTour]);

  const onModalClose = useCallback(() => {
    navigate({ search: "" }, { replace: true });
    setPreviewId("");
  }, [navigate]);

  const assessmentClosed = assessmentRound.assessmentClosed || false;

  const assignedAssessmentQuery = useQuery(
    initUseQuery(api.challenges.getAssignedAssessments, [challenge?._id], {
      isEnabled: !!user?._id,
    }),
  );
  const currentRoundAssessmentIdeas = useMemo(() => {
    return (assignedAssessmentQuery.data?.ideas || []).filter((idea) =>
      (idea.assessments || []).some(
        (assessment) =>
          assessment.user === user?._id &&
          assessment.assessmentRoundId === assessmentRound.id,
      ),
    );
  }, [user, assignedAssessmentQuery.data?.ideas, assessmentRound.id]);

  const assessmentIdeaIds = useMemo(
    () => currentRoundAssessmentIdeas.map((a) => a._id),
    [currentRoundAssessmentIdeas],
  );

  const userId = user?._id;
  const assignedAssessments = useMemo(() => {
    return currentRoundAssessmentIdeas.flatMap((idea) =>
      (idea.assessments || []).flatMap((assessment) =>
        assessment.user === userId &&
        assessment.assessmentRoundId === assessmentRound.id
          ? [{ ...assessment, idea }]
          : [],
      ),
    );
  }, [userId, currentRoundAssessmentIdeas, assessmentRound.id]);

  const shouldShowTour = useMemo(
    () => !!challenge?._id && !!currentRoundAssessmentIdeas.length,
    [challenge, currentRoundAssessmentIdeas],
  );
  const tourIdea = useMemo(
    () => currentRoundAssessmentIdeas[0]?._id ?? "",
    [currentRoundAssessmentIdeas],
  );

  const currentPreviewIndex = useMemo(
    () => (previewId ? assessmentIdeaIds.indexOf(previewId) : -1),
    [assessmentIdeaIds, previewId],
  );
  const navigateNextPreviousIdea = useCallback(
    (direction: 1 | -1) => {
      const nextId = assessmentIdeaIds[currentPreviewIndex + direction];
      if (nextId) {
        setPreviewId(nextId);
      }
    },
    [currentPreviewIndex, assessmentIdeaIds],
  );

  const showBusinessProfile = useMemo(() => {
    return !!ownerIdeaTemplate?.body?.some(
      (section) =>
        section.type === "businessProfile" && section.visibility !== "managers",
    );
  }, [ownerIdeaTemplate]);

  const totalAssessableFields = useMemo(() => {
    return (
      ownerIdeaTemplate?.body
        ?.filter(
          (section) =>
            section.type === "assessment" &&
            section.assessmentRoundId === assessmentRound.id,
        )
        .reduce(
          (count, section) =>
            count +
            section.fields.filter((field) => util.isAssessableField(field))
              .length,
          0,
        ) || 0
    );
  }, [ownerIdeaTemplate, assessmentRound.id]);

  const unsubmittedAssessments = useMemo(
    () =>
      currentRoundAssessmentIdeas.filter((assessment) =>
        (assessment.assessments || []).find(
          (ideaAssessment) =>
            ideaAssessment.isSubmitted !== true &&
            ideaAssessment.assessmentRoundId === assessmentRound.id,
        ),
      ),
    [currentRoundAssessmentIdeas, assessmentRound.id],
  );

  const submissableAssessments = useMemo(
    () =>
      unsubmittedAssessments.filter((assessment) => {
        const myAssessment = assessment.assessments?.find(
          (ideaAssessment) =>
            ideaAssessment.assessmentRoundId === assessmentRound.id &&
            ideaAssessment.user === userId,
        );
        const assessmentIsSubmissable = util.assessmentIsSubmissable(
          myAssessment,
          ownerIdeaTemplate,
        );
        return assessmentIsSubmissable;
      }),
    [unsubmittedAssessments, userId, ownerIdeaTemplate, assessmentRound.id],
  );

  const unsubmittableAssessments = useMemo(
    () =>
      unsubmittedAssessments.filter((assessment) => {
        const myAssessment = assessment.assessments?.find(
          (ideaAssessment) =>
            ideaAssessment.assessmentRoundId === assessmentRound.id &&
            ideaAssessment.user === userId,
        );
        const assessmentIsSubmissable = util.assessmentIsSubmissable(
          myAssessment,
          ownerIdeaTemplate,
        );
        return !assessmentIsSubmissable;
      }),
    [unsubmittedAssessments, userId, ownerIdeaTemplate, assessmentRound.id],
  );

  const downloadReport = () => {
    if (!user?._id) return;

    const challengeName =
      challenge?.name || t("common:capitalise", { key: "generic.challenge" });

    let displayName = `${challengeName} assessment report`;
    if (assessmentRound) {
      displayName += ` - ${assessmentRound.name}`;
    }

    api.users.createDownload(
      user._id,
      {
        downloadType: "userAssessmentXlsxExport",
        displayName: `${displayName}.xlsx`,
        challenge: challenge._id,
        assessmentRoundId: assessmentRound?.id,
      },
      (download) => {
        api.setQueryData(
          api.users.getDownloads,
          [user._id],
          (prev) => ({
            ...prev,
            downloads: [download, ...(prev?.downloads || [])],
          }),
          { allowUndefined: true },
        );
        setTrayStatus({ open: true, activeIndex: 1 });
      },
      () => {},
    );
  };

  const submitAssessment = (ideaId: string) => {
    util
      .confirm(
        "Ready to submit your assessment?",
        `Once submitted you will not be able to change your assessment of this ${t("generic.idea")}.`,
      )
      .then(() =>
        api.ideas.submitAssessment(
          ideaId,
          assessmentRound.id,
          () => {
            toast.success("Assessment submitted");
            api.invalidateQueries(api.challenges.getAssignedAssessments, [
              challenge?._id,
            ]);
          },
          (err) => {
            toast.error(`Unable to submit your assessment: ${err.message}`);
          },
        ),
      )
      .catch(() => {});
  };

  const submitAllAssessments = useCallback(() => {
    util
      .confirm(
        "Ready to submit your assessments?",
        `Once submitted you will not be able to change any of your assessments in this ${t("generic.challenge")}. Only completed assessments will be submitted.`,
      )
      .then(() => {
        api.challenges.submitAllAssessments(
          challenge?._id,
          assessmentRound.id,
          ({ submittedAssessmentIdeaIds }) => {
            toast.success("Assessments submitted");
            api.setQueryData(
              api.challenges.getAssignedAssessments,
              [challenge?._id],
              (prev) => {
                const newIdeas = prev.ideas.map((ideaAssessment) => {
                  if (
                    submittedAssessmentIdeaIds.indexOf(ideaAssessment._id) > -1
                  ) {
                    return {
                      ...ideaAssessment,
                      assessments: ideaAssessment.assessments?.map((prev) => ({
                        ...prev,
                        isSubmitted:
                          prev.assessmentRoundId === assessmentRound.id
                            ? true
                            : prev?.isSubmitted,
                      })),
                    };
                  }
                  return ideaAssessment;
                });
                return { ...prev, ideas: newIdeas };
              },
            );
          },
          (err) =>
            toast.error(`Unable to submit your assessment: ${err.message}`),
        );
      })
      .catch(() => {});
  }, [challenge, assessmentRound.id, t]);

  const checkAllSubmissibility = useCallback(() => {
    if (submissableAssessments.length === unsubmittedAssessments.length) {
      submitAllAssessments();
    } else {
      setAssessmentSubmissionModalOpen(true);
    }
  }, [submitAllAssessments, unsubmittedAssessments, submissableAssessments]);

  if (assignedAssessments.length === 0) {
    return <Message>No assessments assigned in this round.</Message>;
  }
  const displayFields = (ownerIdeaTemplate?.body || [])
    .filter((section) => section.type != "assessment")
    .flatMap((section) =>
      section.fields.filter((field) => field.isDisplayField),
    );

  return (
    <>
      <HandledIdeaPreview
        ideaId={previewId}
        onCloseModal={() => onModalClose()}
        canGoNextIdea={
          currentPreviewIndex < currentRoundAssessmentIdeas.length - 1
        }
        canGoPrevIdea={currentPreviewIndex > 0}
        moveIdea={(direction) =>
          navigateNextPreviousIdea(direction === "next" ? 1 : -1)
        }
        totalIdeas={currentRoundAssessmentIdeas.length}
        currentIdeaPosition={currentPreviewIndex + 1}
        page={1}
        limit={currentRoundAssessmentIdeas.length}
      />
      <ProductTour
        tour="assessment"
        isReady={shouldShowTour}
        customTourFunctions={{
          openIdea: () => setPreviewId(tourIdea),
          closeIdea: () => {
            onModalClose();
          },
        }}
        onComplete={() => setPreviewId(null)}
      />
      <Modal
        open={assessmentSubmissionModalOpen}
        onClose={() => setAssessmentSubmissionModalOpen(false)}
      >
        <ModalHeader>Assessment submission</ModalHeader>
        <ModalContent>
          <H4>
            {" "}
            You have{" "}
            {util.pluralise(
              submissableAssessments?.length || 0,
              "completed assessment",
              "completed assessments",
            )}{" "}
            and{" "}
            {util.pluralise(
              unsubmittableAssessments?.length || 0,
              "oustanding assessment",
              "oustanding assessments",
            )}
            . Only completed assessments will be submitted.
          </H4>
          <p>
            Below are the assessments that will <b>not</b> be included in your
            submission:
          </p>
          <List variant="bulleted" compact>
            {unsubmittableAssessments?.map((a) => {
              return <ListItem key={a._id}>{a.name}</ListItem>;
            })}
          </List>
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setAssessmentSubmissionModalOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={submissableAssessments?.length === 0}
            onClick={() => {
              setAssessmentSubmissionModalOpen(false);
              submitAllAssessments();
            }}
            color="primary"
          >
            <Icon icon={CheckIcon} />
            Submit only the completed assessments
          </Button>
        </ModalActions>
      </Modal>
      <div>
        <AssessmentClosureInfo
          ideaTemplate={ownerIdeaTemplate}
          assessmentRound={assessmentRound}
        />
        <Table
          unstackable
          stickyLastColumn={!theme.sizes.isMobile}
          className="assessment-tour-step-1"
        >
          <TableHeader>
            <TableRow>
              <TableHeaderCell>
                {t("common:capitalise", { key: "generic.idea" })}
              </TableHeaderCell>
              <TableHeaderCell style={{ whiteSpace: "nowrap" }}>
                Total score
                <Popup
                  position="top right"
                  trigger={
                    <Icon
                      icon={CircleQuestionMarkIcon}
                      style={{ marginLeft: 5 }}
                    />
                  }
                >
                  Automatically calculated based on your assessment & predefined
                  section weightings
                </Popup>
              </TableHeaderCell>
              <TableHeaderCell>Progress</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              {displayFields?.length > 0 ? (
                <TableHeaderCell>
                  {t("common:capitalise", { key: "generic.idea" })} content
                </TableHeaderCell>
              ) : null}
              {!theme.sizes.isMobile && <TableHeaderCell />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {assignedAssessments?.map((myAssessment, index) => {
              const declaredCoi =
                myAssessment?.conflictOfInterest?.hasConflictOfInterest !==
                undefined;
              const assessmentIsSubmissable = util.assessmentIsSubmissable(
                myAssessment,
                ownerIdeaTemplate,
              );
              const completedFields = Object.values(
                myAssessment?.assessment || {},
              ).filter(
                (field: any) =>
                  (field.value !== undefined && field.value !== "") ||
                  (field.score !== undefined && field.score !== ""),
              ).length;
              const progress = completedFields / totalAssessableFields;

              return (
                <TableRow
                  key={myAssessment.idea?._id + myAssessment.assessmentRoundId}
                  className={"assessment-tour-step-2"}
                >
                  <TableCell width="5">
                    <FakeLink
                      onClick={() => {
                        setAssessmentRound(assessmentRound?.id);
                        setPreviewId(myAssessment.idea?._id);
                      }}
                    >
                      {myAssessment.idea?.name}
                    </FakeLink>
                    {showBusinessProfile &&
                      myAssessment.idea.lockedOwnerIdeaBusinessProfile && (
                        <p style={{ marginTop: 3 }}>
                          <IdeaBusinessProfileChip
                            compact="very"
                            style={{ maxWidth: 250 }}
                            ideaBusinessProfile={
                              myAssessment.idea.lockedOwnerIdeaBusinessProfile
                            }
                          ></IdeaBusinessProfileChip>
                        </p>
                      )}
                  </TableCell>
                  <TableCell>
                    {myAssessment?.conflictOfInterest?.hasConflictOfInterest &&
                    myAssessment?.isSubmitted ? (
                      "N/A"
                    ) : (
                      <>{myAssessment?.lockedAssessmentScore ?? 0} %</>
                    )}
                  </TableCell>

                  <TableCell>
                    {myAssessment?.conflictOfInterest?.hasConflictOfInterest ? (
                      <div>
                        <Popup
                          offset={[-10, 0]}
                          trigger={
                            <span>
                              <CircularProgress
                                size={20}
                                color="#dc3545"
                                progress={1}
                                strokeWidth={1.5}
                                icon={
                                  completedFields === totalAssessableFields ? (
                                    <Icon icon={CircleAlertIcon} color="red" />
                                  ) : null
                                }
                              />
                            </span>
                          }
                        >
                          {
                            <Label size="small" color="red" basic>
                              Declared conflict of interest
                            </Label>
                          }
                        </Popup>
                      </div>
                    ) : (
                      <div>
                        <Popup
                          offset={[-10, 0]}
                          trigger={
                            <span>
                              <CircularProgress
                                size={20}
                                color={
                                  declaredCoi && progress === 1
                                    ? "#28a745"
                                    : "#007bff"
                                }
                                progress={declaredCoi ? progress : 0}
                                strokeWidth={1.5}
                                icon={
                                  declaredCoi &&
                                  completedFields === totalAssessableFields ? (
                                    <Icon icon={CheckIcon} color="green" />
                                  ) : null
                                }
                              />
                            </span>
                          }
                        >{`${declaredCoi ? completedFields : 0} / ${totalAssessableFields} questions completed`}</Popup>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {myAssessment?.isSubmitted ? (
                      <Label size="small" color="green">
                        Submitted
                      </Label>
                    ) : (
                      <Label size="small" color="orange">
                        Not submitted
                      </Label>
                    )}
                  </TableCell>
                  {displayFields?.length > 0 ? (
                    <TableCell>
                      <IdeaDisplayFields
                        showBorder={false}
                        fields={displayFields
                          .map((field) => ({
                            id: field.id,
                            title: field.title || "Field",
                            value: String(
                              myAssessment.idea.templated?.[field.id] ?? "",
                            ),
                            type: field.type,
                          }))
                          .filter((field) => field.value)}
                      />
                    </TableCell>
                  ) : null}
                  {!theme.sizes.isMobile && (
                    <TableCell collapsing>
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <IdeaPdfExport
                          assessmentRound={assessmentRound}
                          ideas={[myAssessment.idea]}
                          challenge={challenge}
                          trigger={
                            <Button size="small" variant="outline">
                              <Icon icon={FileTextIcon} />
                            </Button>
                          }
                        />
                        {!myAssessment?.isSubmitted && !assessmentClosed ? (
                          <Button
                            tooltip={
                              assessmentIsSubmissable
                                ? undefined
                                : "To submit an assessment you either have to complete all sections of the assessment or declare a conflict of interest and provide context of your conflict."
                            }
                            disabled={!assessmentIsSubmissable}
                            size="small"
                            onClick={() => {
                              const ideaId = myAssessment.idea?._id;
                              if (ideaId) submitAssessment(ideaId);
                            }}
                            color="primary"
                            variant="outline"
                          >
                            Submit
                          </Button>
                        ) : null}
                        <Button
                          size="small"
                          style={{ whiteSpace: "nowrap" }}
                          className={
                            index === 0 ? "assessment-tour-step-3" : undefined
                          }
                          onClick={() => {
                            setAssessmentRound(assessmentRound?.id);
                            setPreviewId(myAssessment.idea?._id);
                          }}
                          color={
                            !myAssessment?.isSubmitted ? "primary" : undefined
                          }
                        >
                          <Icon icon={EyeIcon} />
                          {myAssessment?.isSubmitted
                            ? `View ${t("generic.idea")}`
                            : `Assess ${t("generic.idea")}`}
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          marginTop: 20,
        }}
      >
        <Popup
          trigger={
            <Button
              size="small"
              onClick={() => downloadReport()}
              color="secondary"
              variant="outline"
            >
              <Icon icon={SheetIcon} />
              Download summary
            </Button>
          }
        >
          Generate an excel report containing a summary of your assessments
        </Popup>
        <IdeaPdfExport
          ideas={currentRoundAssessmentIdeas}
          assessmentRound={assessmentRound}
          hidePopup
          challenge={challenge}
          trigger={
            <Button size="small" color="secondary" variant="outline">
              <Icon icon={FileTextIcon} />
              {`Download PDF`}
            </Button>
          }
        />
        {unsubmittedAssessments?.length > 0 && (
          <Button
            size="small"
            className="assessment-tour-step-8"
            onClick={() => checkAllSubmissibility()}
            color="primary"
          >
            <Icon icon={CheckIcon} />
            Submit all
          </Button>
        )}
      </div>
    </>
  );
};

export default YourAssessmentRound;
